/**
 * @fileOverview Picture element with provision for <source> element
 * to handle multiple media resources.
 *
 * @example
 * allSources should be an array of objects in this format:
 * [{
    config: [ {
      combinerOptions: {
        w: 943,
        h: 530
      },
      others: '2x'
    } ],
    mediaQuery: '(min-width: 376px)'
  }]
 */

import React from 'react'
import classNames from 'classnames'
import qs from 'query-string'

/**
 * React Picture element
 * @param  {String} options.thumbnail    thumnail URL of the image
 * @param  {Array}  options.imgClasses   classes
 * @param  {Array}  options.imgDataAttrs data attributes which need to be set on
 *                                       img element
 * @param  {Array}  options.sources      list of objects for sources
 * @return {React.PropTypes.element}     Picture element
 */
const Picture = ({
  thumbnail = '',
  imgClasses = {},
  imgContainer = '',
  allSources = []
}) => {

  // Iterate over all sources and get the actual image URLs
  for (var i = 0; i < allSources.length; i++) {
    let source = allSources[i]
    // Set default URL for this source to thumbnail
    source.urlString = thumbnail
    // If thumbnail image url is not on ESPN CDN exit the loop. Keep url as is.
    let notEspnCdn = thumbnail.match(/a(?:\d|ssets)?\.(?:espncdn|espn\.go)\.com\//i) || []
    if (notEspnCdn.length <= 0) {
      break
    }
    // Contnue to next data source if config object is not found
    if (source.hasOwnProperty('config') === false) {
      continue
    }
    // Get list of configs. i.e. there can be multiple sources per image
    let config = source.config || []
    let urls = []
    let urlString = ''
    // Loop over the sources and construct the combiner URL
    for (var j = 0; j < config.length; j++) {
      let url = ''
      let params = []
      let paramString = ''
      // Get current options object
      let current = config[j]
      //console.info('current', current)

      // Get combiner options - convert to query string
      let combinerOptions = current.combinerOptions
      // Get any other options - that might be needed in query string format
      let mqOptions = current.mqOptions
      /**
       * other options are to be used for image sizes like 2x/3x - add them
       * after query string with space
       */
      let others = current.others || ''
      // Append options to params array
      if (typeof combinerOptions !== 'undefined') {
        params.push(qs.stringify(combinerOptions))
      }
      if (typeof mqOptions !== 'undefined') {
        params.push(qs.stringify(mqOptions))
      }
      // Create params string only if params array is not empty
      if (params.length > 0) {
        paramString = params.join('&')
      }
      // Set URL if paramString exists
      if (paramString !== '') {
        url = `${thumbnail}?${paramString}`
      }
      if (url !== '' && others !== '') {
        url = `${url} ${others}`
      }
      // Append urls only if it's not an empty string
      if (url !== '') {
        urls.push(url)
      }
    }
    /**
     * If more than 1 src url is available, join on ,
     * Update urlString property on source with new URL
     */
    if (urls.length > 0) {
      urlString = urls.join(',')
      source.urlString = urlString
    }
    //console.info('==urlString==', urlString)
  }

  // Convert sources to string and set as __html
  // In case of 404, this prevents image nodes
  // from getting removed from the DOM
  let sourcesString = ''
  const lazyLoad = (imgClasses || {}).lazyload === true

  allSources.map(source => {
    sourcesString += `<source ${ lazyLoad ? `data-srcset="${source.urlString}"` : `srcset="${source.urlString}"` } media="${source.mediaQuery || ''}" />`
  })

  sourcesString += `<img class="${classNames(imgClasses)}" data-image-container="${imgContainer}" />`

  return (
    <picture dangerouslySetInnerHTML={{ __html: sourcesString }} />
  )

}

module.exports = Picture



// WEBPACK FOOTER //
// ../app/components/common/Picture.jsx